import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { ConfirmModal, AlertModal } from '../components/Modal';
import './Admin.css';

const LIMIT = 25;

// ==================== FORMAT ====================
function fmt(amount) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount || 0);
}

function shortId(str) {
  if (!str) return '—';
  return str.length > 24 ? str.slice(0, 22) + '…' : str;
}

// ==================== SEARCH BAR ====================
function SearchBar({ value, onChange, onClear, placeholder }) {
  return (
    <div className="admin-search-bar">
      <i className="fa-solid fa-magnifying-glass admin-search-icon" />
      <input
        className="admin-search-input"
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder || 'Rechercher...'}
      />
      {value && (
        <button className="admin-search-clear" onClick={onClear}>
          <i className="fa-solid fa-xmark" />
        </button>
      )}
    </div>
  );
}

// ==================== TAB CIVILS ====================
function PlayersTab({ token }) {
  const [players, setPlayers] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [impersonating, setImpersonating] = useState(null);
  const timeout = useRef(null);

  const fetchPlayers = useCallback(async (off = 0, q = search) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: off });
      if (q.trim().length >= 2) params.set('search', q.trim());
      const res = await axios.get(`/api/admin/players?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlayers(res.data.players || []);
      setTotal(res.data.total || 0);
      setOffset(off);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [token, search]);

  useEffect(() => { fetchPlayers(0, search); }, [search]); // eslint-disable-line

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => { setSearch(val); setOffset(0); }, 400);
  };

  const [confirmPlayer, setConfirmPlayer] = useState(null);
  const [playerAlert, setPlayerAlert] = useState(null);

  const handleImpersonate = (player) => { setConfirmPlayer(player); };

  const doImpersonate = async (player) => {
    setConfirmPlayer(null);
    setImpersonating(player.identifier);
    try {
      const res = await axios.post('/api/admin/impersonate/player',
        { identifier: player.identifier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.setItem('fleeca_token', res.data.token);
      window.location.href = '/dashboard';
    } catch (e) {
      setPlayerAlert('Erreur : ' + (e.response?.data?.error || e.message));
      setImpersonating(null);
    }
  };

  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <>
    {confirmPlayer && (
      <ConfirmModal
        title="Connexion en tant que joueur"
        message={`Se connecter en tant que "${confirmPlayer.name}" ? Tu vas être redirigé vers son dashboard.`}
        confirmLabel="Se connecter"
        confirmVariant="primary"
        onConfirm={() => doImpersonate(confirmPlayer)}
        onCancel={() => setConfirmPlayer(null)}
      />
    )}
    {playerAlert && (
      <AlertModal message={playerAlert} onClose={() => setPlayerAlert(null)} />
    )}
    <div className="admin-tab-content">
      <div className="admin-tab-header">
        <div>
          <h2 className="admin-tab-title"><i className="fa-solid fa-users" /> Civils</h2>
          <p className="admin-tab-count">{total} personnage{total > 1 ? 's' : ''}</p>
        </div>
        <SearchBar
          value={searchInput}
          onChange={handleSearchInput}
          onClear={() => { setSearchInput(''); setSearch(''); }}
          placeholder="Nom, IBAN, identifier..."
        />
      </div>

      {error && <div className="admin-error"><i className="fa-solid fa-triangle-exclamation" /> {error}</div>}

      {loading ? (
        <div className="admin-loading"><i className="fa-solid fa-circle-notch fa-spin" /> Chargement...</div>
      ) : players.length === 0 ? (
        <div className="admin-empty"><i className="fa-solid fa-user-slash" /> Aucun résultat</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Personnage</th>
                <th>IBAN</th>
                <th>Solde</th>
                <th>Métier</th>
                <th>Identifier</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {players.map(p => (
                <tr key={p.identifier} className="admin-row">
                  <td>
                    <div className="admin-player-name">
                      <div className="admin-avatar">
                        {(p.name[0] || '?').toUpperCase()}
                      </div>
                      <span>{p.name}</span>
                    </div>
                  </td>
                  <td><span className="admin-mono">{p.iban || '—'}</span></td>
                  <td><span className="admin-balance">{fmt(p.bankBalance)}</span></td>
                  <td><span className="admin-job">{p.job}</span></td>
                  <td><span className="admin-mono admin-small">{shortId(p.identifier)}</span></td>
                  <td>
                    <button
                      className="admin-btn-impersonate"
                      onClick={() => handleImpersonate(p)}
                      disabled={impersonating === p.identifier}
                    >
                      {impersonating === p.identifier
                        ? <><i className="fa-solid fa-circle-notch fa-spin" /> Connexion...</>
                        : <><i className="fa-solid fa-right-to-bracket" /> Accéder</>
                      }
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="admin-pagination">
          <button className="admin-pag-btn" disabled={offset === 0} onClick={() => fetchPlayers(offset - LIMIT, search)}>
            <i className="fa-solid fa-chevron-left" /> Préc.
          </button>
          <span className="admin-pag-info">{currentPage} / {pages}</span>
          <button className="admin-pag-btn" disabled={offset + LIMIT >= total} onClick={() => fetchPlayers(offset + LIMIT, search)}>
            Suiv. <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      )}
    </div>
    </>
  );
}

// ==================== TAB ENTREPRISES ====================
function CompaniesTab({ token }) {
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [impersonating, setImpersonating] = useState(null);
  const timeout = useRef(null);

  const fetchCompanies = useCallback(async (off = 0, q = search) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: off });
      if (q.trim().length >= 2) params.set('search', q.trim());
      const res = await axios.get(`/api/admin/companies?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(res.data.companies || []);
      setTotal(res.data.total || 0);
      setOffset(off);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [token, search]);

  useEffect(() => { fetchCompanies(0, search); }, [search]); // eslint-disable-line

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => { setSearch(val); setOffset(0); }, 400);
  };

  const [confirmCompany, setConfirmCompany] = useState(null);
  const [companyAlert, setCompanyAlert] = useState(null);

  const handleImpersonate = (company) => { setConfirmCompany(company); };

  const doImpersonate = async (company) => {
    setConfirmCompany(null);
    setImpersonating(company.account_name);
    try {
      const res = await axios.post('/api/admin/impersonate/company',
        { account_name: company.account_name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.setItem('fleeca_token', res.data.token);
      window.location.href = '/company';
    } catch (e) {
      setCompanyAlert('Erreur : ' + (e.response?.data?.error || e.message));
      setImpersonating(null);
    }
  };

  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <>
    {confirmCompany && (
      <ConfirmModal
        title="Accéder au compte entreprise"
        message={`Accéder au compte de "${confirmCompany.society_name || confirmCompany.account_name}" ? Tu vas être redirigé vers la page entreprise.`}
        confirmLabel="Accéder"
        confirmVariant="primary"
        onConfirm={() => doImpersonate(confirmCompany)}
        onCancel={() => setConfirmCompany(null)}
      />
    )}
    {companyAlert && (
      <AlertModal message={companyAlert} onClose={() => setCompanyAlert(null)} />
    )}
    <div className="admin-tab-content">
      <div className="admin-tab-header">
        <div>
          <h2 className="admin-tab-title"><i className="fa-solid fa-building" /> Entreprises</h2>
          <p className="admin-tab-count">{total} entreprise{total > 1 ? 's' : ''}</p>
        </div>
        <SearchBar
          value={searchInput}
          onChange={handleSearchInput}
          onClear={() => { setSearchInput(''); setSearch(''); }}
          placeholder="Nom, compte, IBAN..."
        />
      </div>

      {error && <div className="admin-error"><i className="fa-solid fa-triangle-exclamation" /> {error}</div>}

      {loading ? (
        <div className="admin-loading"><i className="fa-solid fa-circle-notch fa-spin" /> Chargement...</div>
      ) : companies.length === 0 ? (
        <div className="admin-empty"><i className="fa-solid fa-building-circle-xmark" /> Aucun résultat</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Entreprise</th>
                <th>Compte</th>
                <th>IBAN</th>
                <th>Caisse</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {companies.map(c => (
                <tr key={c.account_name} className="admin-row">
                  <td>
                    <div className="admin-player-name">
                      <div className="admin-avatar admin-avatar-company">
                        <i className="fa-solid fa-building" />
                      </div>
                      <span>{c.society_name || c.account_name}</span>
                    </div>
                  </td>
                  <td><span className="admin-mono admin-small">{c.account_name}</span></td>
                  <td><span className="admin-mono">{c.iban || '—'}</span></td>
                  <td><span className="admin-balance">{fmt(c.money)}</span></td>
                  <td>
                    <button
                      className="admin-btn-impersonate admin-btn-company"
                      onClick={() => handleImpersonate(c)}
                      disabled={impersonating === c.account_name}
                    >
                      {impersonating === c.account_name
                        ? <><i className="fa-solid fa-circle-notch fa-spin" /> Connexion...</>
                        : <><i className="fa-solid fa-right-to-bracket" /> Accéder</>
                      }
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="admin-pagination">
          <button className="admin-pag-btn" disabled={offset === 0} onClick={() => fetchCompanies(offset - LIMIT, search)}>
            <i className="fa-solid fa-chevron-left" /> Préc.
          </button>
          <span className="admin-pag-info">{currentPage} / {pages}</span>
          <button className="admin-pag-btn" disabled={offset + LIMIT >= total} onClick={() => fetchCompanies(offset + LIMIT, search)}>
            Suiv. <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      )}
    </div>
    </>
  );
}

// ==================== ADMIN PAGE ====================
const TABS = [
  { key: 'players', label: 'Civils', icon: 'fa-users' },
  { key: 'companies', label: 'Entreprises', icon: 'fa-building' },
];

export default function Admin() {
  const { token, user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('players');

  useEffect(() => {
    if (!loading && (!user || !user.isAdmin)) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="admin-loading-screen">
        <i className="fa-solid fa-circle-notch fa-spin" />
      </div>
    );
  }

  if (!user.isAdmin) return null;

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-inner">
          <div className="admin-header-left">
            <div className="admin-logo">
              <i className="fa-solid fa-shield-halved" />
            </div>
            <div>
              <h1 className="admin-title">Panel Admin</h1>
              <p className="admin-subtitle">Fleeca Bank — Nexa RP</p>
            </div>
          </div>
          <div className="admin-header-right">
            <div className="admin-badge-admin">
              <i className="fa-solid fa-crown" />
              Administrateur
            </div>
            <button className="admin-btn-back" onClick={() => navigate('/')}>
              <i className="fa-solid fa-arrow-left" /> Retour
            </button>
          </div>
        </div>
      </div>

      {/* Bandeau impersonation warning */}
      <div className="admin-warn-bar">
        <i className="fa-solid fa-triangle-exclamation" />
        Accès administrateur — toute action est tracée. En cliquant sur "Accéder", tu seras redirigé vers le compte du joueur ou de l'entreprise choisie.
      </div>

      {/* Tabs */}
      <div className="admin-container">
        <div className="admin-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`admin-tab-btn ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              <i className={`fa-solid ${t.icon}`} />
              {t.label}
            </button>
          ))}
        </div>

        <div className="admin-panel">
          {tab === 'players' && <PlayersTab token={token} />}
          {tab === 'companies' && <CompaniesTab token={token} />}
        </div>
      </div>
    </div>
  );
}
